import { api } from 'services/api';
import { createRestProvider } from './restProvider';

const dataProvider = createRestProvider();

interface OrganizationApi {
  id: number;
  code?: string;
  name?: string;
  alias?: string;
  description?: string;
  isPublic?: boolean;
  productVisibility?: string[];
  rateCodePriorities?: string[];
  displayCurrency?: string;
  updatedAt?: string;
  tourPlan?: { serverId?: string; serverPassword?: string };
  branding?: {
    logo?: string;
    primaryColour?: string;
    secondaryPrimaryColour?: string;
    secondaryColour?: string;
  };
}

type FormValues = Record<string, any>;

/**
 * Form field names are flat dotted keys (e.g.
 * `organization_providers.data.server_id`), kept from the Hasura-based form.
 */
const toApiBody = (values: FormValues, includeCode: boolean) => {
  const body: Record<string, unknown> = {
    name: values.name,
    description: values.description,
    productVisibility: values.product_visibility,
    rateCodePriorities: values.rate_code_priorities
  };
  if (includeCode) body.code = values.code;

  const serverId = values['organization_providers.data.server_id'];
  const serverPassword = values['organization_providers.data.server_password'];
  if (serverId || serverPassword) {
    body.tourPlan = { serverId, serverPassword };
  }
  return body;
};

const fromApi = (org: OrganizationApi) => ({
  id: org.id,
  code: org.code,
  name: org.name,
  description: org.description,
  product_visibility: org.productVisibility || [],
  rate_code_priorities: org.rateCodePriorities || [],
  branding: org.branding,
  'organization_providers.data.server_id': org.tourPlan?.serverId,
  'organization_providers.data.server_password': org.tourPlan?.serverPassword
});

dataProvider.getOne = async ({ id }) => {
  const { data } = await api.get<{ data: OrganizationApi }>(
    `/admin/organizations/${id}`
  );
  return { data: fromApi(data) as any };
};

dataProvider.create = async ({ variables }) => {
  const { data } = await api.post<{ data: OrganizationApi }>(
    '/admin/organizations',
    toApiBody(variables as FormValues, true)
  );
  return { data: fromApi(data) as any };
};

dataProvider.update = async ({ id, variables }) => {
  const { data } = await api.patch<{ data: OrganizationApi }>(
    `/admin/organizations/${id}`,
    toApiBody(variables as FormValues, true)
  );
  return { data: fromApi(data) as any };
};

export const organizationsDataProvider = dataProvider;
